import styled from '@emotion/styled';
import { PrimaryButton } from '@react-pdf-viewer/core';
import { StyledDiv } from '../../common/components/styled-div';
import { Icon } from '../../atoms/icon/icon';
import {
  Typography,
  Spacing,
} from '../../atoms/progress-with-label/progress-with-label';
import { TBProgressBar } from '../tb-progress-bar/tb-progress-bar';
import { TrainingStatus } from '@taleemabad/shared';
export interface ProgressListCardProps {
  level?: number;
  levelName?: string;
  numberOfCourses?: number;
  progressPercentage: number;
  bottomCardIcon?: string;
  bottomCardText?: string;
  courseCountIcon?: string;
  levelStatus: TrainingStatus;
  onClick?: () => void;
  cardOnClick?: () => void;
  onAttemptExamClick?: () => void;
  disableAttemptExam?: boolean;
  primaryButtonText?: string;
  dataCy?: string;
  buttonDataCy?: string;
  isCertified?: boolean;
  onViewCertificate?: () => void;
}

const ProgressListCardWrap = styled.div`
  background: var(--md-sys-color-surface);
  padding: var(--rk-spacing-xl);
  border-radius: var(--rk-spacing-lg);
  border: 1px solid var(--md-sys-color-light-outline-variant);
  position: relative;
  overflow: hidden;
  flex: 1;
`;
const DisableCard = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.5);
  z-index: 5;
  pointer-events: none;
`;

const fontColors = {
  [TrainingStatus.InProgress]: 'textLabelMediumSurfaceVariantError',
  [TrainingStatus.Completed]: 'textLabelMediumSurfaceVariantSuccess',
  [TrainingStatus.CourseCompleted]: 'textLabelMediumSurfaceVariantSuccess',
  [TrainingStatus.Locked]: 'textLabelMedium',
} as const;

const getFontColor = (status: TrainingStatus) => {
  return fontColors[status] ?? fontColors[TrainingStatus.Locked];
};
export const ProgressListCard = ({
  level,
  levelName,
  numberOfCourses,
  progressPercentage,
  bottomCardIcon,
  bottomCardText,
  courseCountIcon,
  levelStatus,
  cardOnClick,
  onClick,
  onAttemptExamClick,
  disableAttemptExam,
  primaryButtonText,
  dataCy,
  buttonDataCy,
  isCertified,
  onViewCertificate,
}: ProgressListCardProps) => {
  return (
    <StyledDiv position="relative" width="100%">
      {levelStatus === TrainingStatus.Locked && <DisableCard />}
      <ProgressListCardWrap
        onClick={
          levelStatus !== TrainingStatus.Locked ? cardOnClick : undefined
        }
        data-cy={dataCy || 'training-course-card'}
      >
        <StyledDiv
          flexDirection="row"
          justifyContent="space-between"
          alignItem="center"
          zIndex={3}
          position="relative"
        >
          <Typography variant={getFontColor(levelStatus)}>
            {levelStatus}
          </Typography>
          {levelStatus === TrainingStatus.Locked && <Icon name="Lock" />}
        </StyledDiv>
        <Spacing marginTop="md" />
        <StyledDiv
          flexDirection="column"
          justifyContent="start"
          alignItem="start"
          gap={2}
        >
          <Typography variant="textBodyLargeLightOnSurface">
            {levelName}
          </Typography>
        </StyledDiv>

        {progressPercentage > 0 && (
          <TBProgressBar progress={progressPercentage} />
        )}

        <Spacing marginTop="xl" />
        {level && levelStatus !== TrainingStatus.Locked && (
          <StyledDiv
            flexDirection="row"
            justifyContent="start"
            gap={8}
            alignItem="center"
          >
            {!isCertified && onAttemptExamClick != null && (
              <StyledDiv
                padding="var(--rk-spacing-md) var(--rk-spacing-lg)"
                onClick={onAttemptExamClick}
                disabled={disableAttemptExam}
                data-cy="grand-quiz-button"
              >
                <Typography variant="textLabelLargePrimary60">
                  Attempt Exam
                </Typography>
              </StyledDiv>
            )}
            {isCertified ? (
              <>
                <StyledDiv
                  padding="var(--rk-spacing-md) var(--rk-spacing-lg)"
                  borderRadius="--rk-spacing-xxxxxxxxxxxxl"
                  background="--md-sys-light-primary"
                  onClick={onViewCertificate}
                  data-cy="view-certificate-button"
                >
                  <Typography variant="textLabelLargeOnPrimary">
                    Download Certificate
                  </Typography>
                </StyledDiv>
                {onClick != null && (
                  <StyledDiv
                    padding="var(--rk-spacing-md) var(--rk-spacing-lg)"
                    onClick={onClick}
                    data-cy={buttonDataCy}
                  >
                    <Typography variant="textLabelLargePrimary60">
                      Watch Again
                    </Typography>
                  </StyledDiv>
                )}
              </>
            ) : (
              <StyledDiv
                padding="var(--rk-spacing-md) var(--rk-spacing-lg)"
                borderRadius="--rk-spacing-xxxxxxxxxxxxl"
                background="--md-sys-light-primary"
                onClick={onClick}
                data-cy={buttonDataCy}
              >
                <Typography variant="textLabelLargeOnPrimary">
                  {primaryButtonText ? primaryButtonText : 'Start Trainings'}
                </Typography>
              </StyledDiv>
            )}
          </StyledDiv>
        )}
        {bottomCardIcon && bottomCardText && (
          <StyledDiv flexDirection="row" gap={8}>
            <Icon size="regular" name={bottomCardIcon} />
            <Typography variant="textBodyMediumLightOnSurface">
              {bottomCardText}
            </Typography>
          </StyledDiv>
        )}
      </ProgressListCardWrap>
    </StyledDiv>
  );
};
